import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={1} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            SuperChargeMe ("we," "our," or "us") operates the SuperChargeMe Data Platform (the "Service"), 
            a business analytics and data integration platform that helps businesses connect, analyze, and 
            optimize their marketing, e-commerce, and financial data from multiple sources.
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Policy describes how we collect, use, disclose, and safeguard your information 
            when you use our Service, including data obtained through integrations with third-party platforms 
            such as Google Ads, Facebook Marketing, Amazon, Shopify, Pinterest, TikTok, Slack, QuickBooks, 
            and other business applications.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>GDPR Compliance:</strong> This Privacy Policy complies with the General Data Protection 
            Regulation (GDPR) and other applicable data protection laws. If you are located in the European 
            Union (EU) or European Economic Area (EEA), you have specific rights regarding your personal data 
            as outlined in Section 7 below.
          </Typography>
        </Box>

        {/* Information We Collect */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            2. Information We Collect
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            2.1 Information You Provide Directly
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Account information: name, email address, company information, and authentication credentials" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Business data: brand information, store details, and organizational settings" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Configuration data: integration settings, data mapping preferences, and dashboard customizations" />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            2.2 Data from Third-Party Platform Integrations
          </Typography>
          <Typography variant="body1" paragraph>
            When you connect third-party platforms to our Service, we collect and process data from these platforms 
            in accordance with the permissions you grant during the OAuth authorization process:
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            E-commerce Platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Shopify" 
                secondary="Order data, product information, customer details, sales analytics, inventory data" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Amazon Seller Partner" 
                secondary="Sales reports, product performance, order fulfillment data, inventory levels" 
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Advertising Platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Google Ads" 
                secondary="Campaign performance, ad spend, click-through rates, conversion data, keyword analytics" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Facebook Marketing" 
                secondary="Ad campaign metrics, audience insights, engagement data, conversion tracking" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Pinterest Business" 
                secondary="Pin performance, board analytics, advertising metrics, audience demographics" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="TikTok for Business" 
                secondary="Ad campaign performance, video engagement metrics, audience insights" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Amazon Advertising" 
                secondary="Sponsored product performance, keyword bidding data, campaign analytics" 
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Google Services (in addition to Google Ads above):
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Google Analytics"
                secondary="Web/app session metrics, traffic sources, conversion events, audience insights, GA4 property and view metadata"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Search Console"
                secondary="Search performance data (queries, impressions, clicks, CTR, position), indexing status, sitemap data for verified sites"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Merchant Center"
                secondary="Product feed metadata, item-level performance, account and store information"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Workspace (Drive, Docs, Sheets)"
                secondary="Files and folders you explicitly grant access to, used for document export, reporting, and shared analytics workflows. We access only the files you authorize and do not browse your wider Drive."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Microsoft Services:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Microsoft Advertising"
                secondary="Campaign performance, ad spend, keyword data, conversion metrics"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Microsoft Teams"
                secondary="Channel and team metadata, message analytics (when authorized) for collaboration features"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Additional Advertising and Commerce Platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="TikTok Shop, TikTok Creator (in addition to TikTok for Business above)"
                secondary="Product catalog, order, and creator-account performance data tied to your TikTok presence"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Attentive"
                secondary="SMS campaign performance, subscriber metrics, message analytics"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Yotpo"
                secondary="Reviews, ratings, loyalty program data, and associated customer metrics"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="ShipHero"
                secondary="Order fulfillment, inventory, warehouse, and shipping performance data (Bearer-token integration)"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Marketing & Communication Platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Klaviyo" 
                secondary="Email campaign metrics, subscriber data, automation performance, customer profiles" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="SMSBump" 
                secondary="SMS campaign data, subscriber information, message delivery metrics" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Slack" 
                secondary="Workspace information, channel data, message analytics (when authorized)" 
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Financial & Accounting Platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="QuickBooks Online"
                secondary="Company information, customer data, invoices, payments, products/services, financial reports, chart of accounts, sales transactions, tax information, and other business financial data"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Xero"
                secondary="Accounting records, invoices, bills, contacts, chart of accounts, bank transactions, and financial reports"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="PayPal, Payoneer, Wise, Revolut"
                secondary="Transaction history, account balances, payment metadata, and currency information used for finance reporting and reconciliation"
              />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            2.3 Automatically Collected Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Usage data: how you interact with our Service, features used, time spent" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Technical data: IP address, browser type, device information, operating system" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Log data: access times, pages viewed, actions taken within the platform" />
            </ListItem>
          </List>
        </Box>

        {/* How We Use Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the collected information for the following purposes, based on the following legal bases under GDPR:
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            3.1 Contract Performance (Article 6(1)(b) GDPR)
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Provide and maintain our Service, including data integration, analytics, and reporting" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Process and analyze your business data to generate insights and recommendations" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Create dashboards, reports, and visualizations based on your connected data sources" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Authenticate and authorize access to third-party platforms (including QuickBooks) on your behalf" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Process accounting and financial data from QuickBooks for business analytics purposes" />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            3.2 Legitimate Interests (Article 6(1)(f) GDPR)
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Improve our Service through analysis of usage patterns and user feedback" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Ensure security and prevent fraudulent or unauthorized access" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Send administrative communications about your account or our Service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Provide customer support and respond to your inquiries" />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            3.3 Legal Obligations (Article 6(1)(c) GDPR)
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Comply with tax, accounting, and financial regulations" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Respond to lawful requests from authorities" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Maintain records for legal and regulatory compliance" />
            </ListItem>
          </List>
        </Box>

        {/* Data Sharing */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            4. How We Share Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="With your explicit consent for specific purposes" />
            </ListItem>
            <ListItem>
              <ListItemText primary="With service providers who assist in operating our platform (hosting, analytics, support)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="When required by law, court order, or government regulation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To protect our legal rights, prevent fraud, or ensure user safety" />
            </ListItem>
            <ListItem>
              <ListItemText primary="In connection with a business transfer, merger, or acquisition" />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            <strong>Third-Party Platform Integration:</strong> Our Service connects to third-party platforms using OAuth 
            authentication. We only access data that you explicitly authorize during the connection process. We do not 
            share your data back to these platforms beyond what is necessary for the integration to function.
          </Typography>
        </Box>

        {/* Google API Services User Data Policy Compliance */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            5. Google API Services User Data Policy Compliance
          </Typography>
          <Typography variant="body1" paragraph>
            SuperChargeMe&apos;s use and transfer of information received from Google APIs to any other app will adhere
            to the{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            5.1 Google APIs We Use and Why
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Google Ads API"
                secondary="Read campaign performance, ad spend, conversion data, and account structure to surface advertising analytics within the SuperChargeMe dashboard."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Analytics (GA4) Admin & Data APIs"
                secondary="Read account, property, and report data to integrate web/app analytics with your wider business reporting."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Search Console API"
                secondary="Read search performance data (queries, impressions, clicks, position) for verified sites you connect."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Merchant Center / Content API for Shopping"
                secondary="Read product catalog, item performance, and account information to integrate Shopping data."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Google Workspace (Drive / Docs / Sheets) APIs"
                secondary="Access only the specific files and folders you authorize, to enable document export, shared reporting, and collaborative analytics workflows. We do not browse your wider Drive contents."
              />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            5.2 Limited Use Commitments
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="We use Google user data only to provide and improve user-facing features within SuperChargeMe (analytics, advertising performance, search performance, merchant catalog, and document/workspace integration)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="We do not transfer Google user data to third parties except as necessary to provide or improve the Service, comply with applicable law, or as part of a merger or acquisition (with continued protections for the data)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="We do not use Google user data to serve advertisements." />
            </ListItem>
            <ListItem>
              <ListItemText primary="We do not allow humans to read Google user data unless we have your affirmative consent for specific items, it is necessary for security purposes (e.g., investigating abuse), to comply with applicable law, or the data has been aggregated and anonymized for internal operations." />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            5.3 Revoking Access
          </Typography>
          <Typography variant="body1" paragraph>
            You may revoke SuperChargeMe&apos;s access to your Google account at any time from within SuperChargeMe
            (Connectors → Disconnect) or from your Google Account at{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
              myaccount.google.com/permissions
            </a>
            . Revocation immediately stops further data collection from the Google APIs.
          </Typography>
        </Box>

        {/* Data Security */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            6. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational security measures to protect your information:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Encryption of data in transit and at rest using industry-standard protocols" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Secure OAuth 2.0 authentication for third-party platform connections" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Access controls and authentication mechanisms for user accounts" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Regular security audits and monitoring of our systems" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Data backup and disaster recovery procedures" />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            While we strive to protect your information, no method of transmission over the internet or electronic 
            storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your data 
            using industry best practices.
          </Typography>
        </Box>

        {/* Data Retention */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            7. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your information for as long as necessary to provide our Service and fulfill the purposes 
            outlined in this Privacy Policy:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Account data: retained while your account is active and for a reasonable period after deletion" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Business data: retained according to your subscription plan and data retention settings" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Third-party platform data: retained based on your integration settings and business needs" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Log data: typically retained for 12 months for security and analytical purposes" />
            </ListItem>
          </List>
        </Box>

        {/* Your Rights */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            8. Your Rights and Choices
          </Typography>
          <Typography variant="body1" paragraph>
            You have the following rights regarding your personal information. If you are located in the EU/EEA, 
            these rights are guaranteed under the GDPR:
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            8.1 GDPR Rights (EU/EEA Residents)
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Right of Access (Article 15)" 
                secondary="Request a copy of the personal information we hold about you, including data from QuickBooks integrations" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Rectification (Article 16)" 
                secondary="Request correction of inaccurate or incomplete information" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Erasure / Right to be Forgotten (Article 17)" 
                secondary="Request deletion of your personal information (subject to legal and contractual obligations)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Data Portability (Article 20)" 
                secondary="Request transfer of your data to another service provider in a structured, machine-readable format" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Object (Article 21)" 
                secondary="Object to processing of your information for certain purposes, including marketing and profiling" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Restrict Processing (Article 18)" 
                secondary="Request limitation of processing in certain circumstances" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Withdraw Consent" 
                secondary="Where processing is based on consent, you can withdraw it at any time" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Lodge a Complaint" 
                secondary="File a complaint with your local data protection authority if you believe your rights have been violated" 
              />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            8.2 How to Exercise Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            To exercise any of these rights, please contact us at <strong>privacy@superchargeme.com</strong> with 
            "Data Subject Request" in the subject line. We will respond to your request within 30 days (or 1 month 
            under GDPR). For complex requests, we may extend this period by up to 2 additional months.
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            8.3 Managing Platform Connections
          </Typography>
          <Typography variant="body1" paragraph>
            You can also manage your third-party platform connections (including QuickBooks) directly through our 
            Service by disconnecting integrations, which will stop further data collection from those platforms. 
            This includes the ability to revoke OAuth access tokens and delete stored integration data.
          </Typography>
        </Box>

        {/* Third-Party Links */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            9. Third-Party Services and Links
          </Typography>
          <Typography variant="body1" paragraph>
            Our Service integrates with various third-party platforms and may contain links to external websites. 
            We are not responsible for the privacy practices or content of these third-party services. We encourage 
            you to review the privacy policies of any third-party platforms you connect to our Service.
          </Typography>
        </Box>

        {/* International Transfers */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            10. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Our servers are hosted on Google Cloud in the United States (us-east1).
            However, some of our third-party service providers and integrated platforms may transfer data internationally.
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            10.1 EU/EEA Data Processing
          </Typography>
          <Typography variant="body1" paragraph>
            Our primary data processing occurs on Google Cloud servers in the United States (us-east1). Your QuickBooks
            and other business data is primarily stored and processed on these servers.
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            10.2 Third-Party Platform Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            When you connect to third-party platforms (like QuickBooks, Google, Facebook, etc.), data may be processed 
            by these platforms according to their own privacy policies and locations. We ensure that:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="All third-party integrations comply with adequate data protection standards" />
            </ListItem>
            <ListItem>
              <ListItemText primary="We use Standard Contractual Clauses (SCCs) where appropriate" />
            </ListItem>
            <ListItem>
              <ListItemText primary="We minimize data transfers to what's necessary for service functionality" />
            </ListItem>
          </List>
        </Box>

        {/* Children's Privacy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            11. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our Service is not intended for children under the age of 16. We do not knowingly collect personal 
            information from children under 16. If we become aware that we have collected personal information 
            from a child under 16, we will take steps to delete such information.
          </Typography>
        </Box>

        {/* Changes to Privacy Policy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            12. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for other 
            operational, legal, or regulatory reasons. We will notify you of any material changes by posting the 
            new Privacy Policy on this page and updating the "Last updated" date.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            13. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            13.1 General Privacy Inquiries
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Company:</strong> SuperchargeMe Inc.<br />
            <strong>Email:</strong> privacy@superchargeme.com<br />
            <strong>Website:</strong> https://app.superchargeme.com<br />
            <strong>Data Controller:</strong> SuperChargeMe Data Platform<br />
            <strong>Server Location:</strong> United States (Google Cloud, us-east1)
          </Typography>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            13.2 GDPR Data Subject Requests
          </Typography>
          <Typography variant="body1" paragraph>
            For EU/EEA residents exercising GDPR rights, please email <strong>privacy@superchargeme.com</strong> 
            with "Data Subject Request" in the subject line. Include:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Your full name and registered email address" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Specific right you wish to exercise" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Details of your request" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Proof of identity (if required)" />
            </ListItem>
          </List>
          
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            13.3 Data Protection Authority
          </Typography>
          <Typography variant="body1" paragraph>
            If you believe your privacy rights have been violated, you have the right to lodge a complaint 
            with your local data protection authority.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="body2" color="text.secondary" align="center">
          This Privacy Policy is effective as of the date listed above and applies to all users of the SuperChargeMe Data Platform.
        </Typography>

      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
